import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    CheckboxFieldType, CurrencyFieldType, DateFieldType, DropDownFieldType,
    IntegerFieldType, RadioButtonsFieldType, RichTextFieldType, ShortTextFieldType
} from '../../../shared/models/form-builder/form-field-types';
import { FormField } from '../../../shared/models/form-builder/form-field.model';
import { FormInstanceElement } from '../../../shared/models/form-builder/form-instance-element.model';
import { FormInstance } from '../../../shared/models/site-content/form-instance.model';
import { SiteResponseManagementData, SiteSubmissionForReview } from '../../../shared/models/site-response-management.model';
import { CurrentSiteService } from '../../../shared/services/current-site.service';
import { FormInstanceService } from '../../../shared/services/form-instance.service';

// Define the data structures used to display the submissions for review table.
/*
class SubmissionForReviewInfo {
    SubmissionId: number = -1;
    SubmissionUser: string;
    SubmissionDate: string;
}
*/

class SubmissionsForReviewDataSource implements DataSource<SiteSubmissionForReview> {
    // Instance data.
    private siteResponseManagementData: SiteResponseManagementData = null;
    private arrSubmissions: SiteSubmissionForReview[] = [];
    private submissionsSubject: BehaviorSubject<SiteSubmissionForReview[]> = new BehaviorSubject<SiteSubmissionForReview[]>([]);

    // Constructor.
    constructor(siteResponseManagementDataParam: SiteResponseManagementData) {
        this.siteResponseManagementData = siteResponseManagementDataParam;

        this.arrSubmissions = this.siteResponseManagementData.submissionsForReview;

        return;
    }
    
    // Implement DataSource (interface) methods.
    public connect(collectionViewer: CollectionViewer): Observable<SiteSubmissionForReview[]> {
        let result: Observable<SiteSubmissionForReview[]> = this.submissionsSubject.asObservable();

        this.submissionsSubject.next(this.arrSubmissions);

        return (result);
    }

    public disconnect(collectionViewer: CollectionViewer): void {
        this.submissionsSubject.complete();

        return;
    }
}

@Component({
    selector: 'app-manage',
    templateUrl: './manage.component.html',
    styleUrls: ['./manage.component.scss'],
    standalone: false
})
export class ManageComponent implements OnInit {
    // Instance data.
    private siteResponseManagementData: SiteResponseManagementData = null;
    private submissionsDataSource: SubmissionsForReviewDataSource = null;
    private readonly astrSubmissionsTableColNames: string[] = ['Submission Id', 'Submission User', 'Submission Date'];

    private astrMetricFieldNames: string[] =
        [
            'distinctRecipientCount',
            'distinctEditorsCount',
            'totalSubmissionsCount',
            'averageResponseTimeInHours'
        ]

    // BEGIN CODE TO REMOVE AFTER TESTING.
    testFormInstance: FormInstance = null;

    testFormField1: FormField = new FormField();
    testFormInstanceElement1 = new FormInstanceElement();
    testFormField2: FormField = new FormField();
    testFormInstanceElement2 = new FormInstanceElement();

    testFormField3: FormField = new FormField();
    testFormInstanceElement3 = new FormInstanceElement();
    testFormField4: FormField = new FormField();
    testFormInstanceElement4 = new FormInstanceElement();

    testFormField5: FormField = new FormField();
    testFormInstanceElement5 = new FormInstanceElement();
    testFormField6: FormField = new FormField();
    testFormInstanceElement6 = new FormInstanceElement();

    testFormField7: FormField = new FormField();
    testFormInstanceElement7 = new FormInstanceElement();
    testFormField8: FormField = new FormField();
    testFormInstanceElement8 = new FormInstanceElement();

    testFormField9: FormField = new FormField();
    testFormInstanceElement9 = new FormInstanceElement();
    testFormField10: FormField = new FormField();
    testFormInstanceElement10 = new FormInstanceElement();

    testFormField11: FormField = new FormField();
    testFormInstanceElement11 = new FormInstanceElement();
    testFormField12: FormField = new FormField();
    testFormInstanceElement12 = new FormInstanceElement();

    testFormField13: FormField = new FormField();
    testFormInstanceElement13 = new FormInstanceElement();
    testFormField14: FormField = new FormField();
    testFormInstanceElement14 = new FormInstanceElement();

    testFormField15: FormField = new FormField();
    testFormInstanceElement15 = new FormInstanceElement();
    testFormField16: FormField = new FormField();
    testFormInstanceElement16 = new FormInstanceElement();

    /*
    testFormGroup: FormGroup = null;
    testFormArray: FormArray = null;
    testFormControl1: FormControl = null;
    testFormControl2: FormControl = null;
    */
    // END CODE TO REMOVE AFTER TESTING.

    constructor(private route: ActivatedRoute,
        private formBuilder: UntypedFormBuilder,
        private readonly titleService: Title,
        private currentSiteService: CurrentSiteService,
        private formInstanceService: FormInstanceService) {
        // If a (test) form instance was loaded, save it.
        if (this.route.snapshot.data) {
            // Check for the site response management data.
            if (this.route.snapshot.data.siteResponseManagementData) {
                this.siteResponseManagementData = this.route.snapshot.data.siteResponseManagementData;

                this.submissionsDataSource = new SubmissionsForReviewDataSource(this.siteResponseManagementData);
            }

            // For the temporary form instance test code,
            // check for the loaded form instance.
            if (this.route.snapshot.data.formInstance) {
                this.testFormInstance = <FormInstance>this.route.snapshot.data.formInstance;
            }
        }

        return;
    }

    ngOnInit() {
        this.titleService.setTitle(this.route.snapshot.data['title'] + ' for ' + this.currentSiteService.Site.name);

        // BEGIN CODE TO REMOVE AFTER TESTING.
        // Load the test form instance as we are no longer 
        // using class FormInstanceResolver to load it.
        /*
        if (this.testFormInstance === null) {
            let id = this.route.snapshot.params['id'];

            if ((id === undefined) || (id === null) || (id.toString().trim() === '')) {
                id = '11'; // This is the ID of an existing form.
            }

            return this.formInstanceService.get(id).then(response => {
                return response;
            });

        }
        */

        // Create data for some test short text components.
        this.testFormField1.fieldDefinitionClassName = ShortTextFieldType; //'Collect.vNext.FieldDefinitions.ShortTextFieldDefinition';
        this.testFormField1.displayName = 'Short Text 1';
        this.testFormField1.required = true;
        this.testFormField1.maxLength = 10;
        this.testFormInstanceElement1.TextValue = 'abc123';

        this.testFormField2.fieldDefinitionClassName = ShortTextFieldType; //'Collect.vNext.FieldDefinitions.ShortTextFieldDefinition';
        this.testFormField2.displayName = 'Short Text 2';
        this.testFormField2.required = true;
        this.testFormField2.regex = '^[0-9][0-9][0-9]-[0-9][0-9][0-9]-[0-9][0-9][0-9][0-9]$'; // A phone number
        this.testFormInstanceElement2.TextValue = 'xyz';

        // Test integer components.
        this.testFormField3.fieldDefinitionClassName = IntegerFieldType; //'Collect.vNext.FieldDefinitions.IntegerFieldDefinition';
        this.testFormField3.displayName = 'Integer 1';
        this.testFormField3.required = true;
        this.testFormField3.minValue = 15;
        this.testFormField3.maxValue = 100;
        this.testFormInstanceElement3.IntValue = 15;

        this.testFormField4.fieldDefinitionClassName = IntegerFieldType; //'Collect.vNext.FieldDefinitions.IntegerFieldDefinition';
        this.testFormField4.displayName = 'Integer 2';
        this.testFormField4.required = true;
        this.testFormField4.maxValue = 50;
        this.testFormInstanceElement4.IntValue = 25;

        // Test paragraph components.
        this.testFormField5.fieldDefinitionClassName = RichTextFieldType; //'Collect.vNext.FieldDefinitions.ParagraphFieldDefinition';
        this.testFormField5.displayName = 'Paragraph 1';
        this.testFormField5.maxLength = 32;
        this.testFormInstanceElement5.TextValue = 'Paragraph 1:  some text ...';

        this.testFormField6.fieldDefinitionClassName = RichTextFieldType; //'Collect.vNext.FieldDefinitions.ParagraphFieldDefinition';
        this.testFormField6.displayName = 'Paragraph 2';
        this.testFormInstanceElement6.TextValue = 'Paragraph 2:  testing 1 2 3 ...';

        // Test check box components.
        this.testFormField7.fieldDefinitionClassName = CheckboxFieldType; //'Collect.vNext.FieldDefinitions.CheckBoxFieldDefinition';
        this.testFormField7.displayName = 'Check Box 1';
        this.testFormInstanceElement7.BooleanValue  = false;

        this.testFormField8.fieldDefinitionClassName = CheckboxFieldType; //'Collect.vNext.FieldDefinitions.CheckBoxFieldDefinition';
        this.testFormField8.displayName = 'Check Box 2';
        this.testFormInstanceElement8.BooleanValue = true;

        // Test date components.
        this.testFormField9.fieldDefinitionClassName = DateFieldType; //'Collect.vNext.FieldDefinitions.DateFieldDefinition';
        this.testFormField9.displayName = 'Date 1';
        this.testFormField9.minValue = 20190101;
        //this.testFormInstanceElement9.textValue = "Fri Nov 01 2019 00:00:00 GMT-0500 (Eastern Standard Time)";
        this.testFormInstanceElement9.TextValue = "2019-11-01T04:00:00.000Z";

        this.testFormField10.fieldDefinitionClassName = DateFieldType; //'Collect.vNext.FieldDefinitions.DateFieldDefinition';
        this.testFormField10.displayName = 'Date 2';
        this.testFormField10.maxValue = 20191231;
        this.testFormField10.defaultValue = "2019-11-14T04:00:00.000Z";
        //this.testFormInstanceElement10.textValue = "2019-11-01T04:00:00.000Z";

        // Test currency components.
        this.testFormField11.fieldDefinitionClassName = CurrencyFieldType; //'Collect.vNext.FieldDefinitions.CurrencyFieldDefinition';
        this.testFormField11.displayName = 'Currency 1';
        this.testFormField11.required = true;
        this.testFormField11.minValue = 100.00;
        this.testFormField11.maxValue = 1000.00;
        this.testFormInstanceElement11.DecimalValue = 0.00;

        this.testFormField12.fieldDefinitionClassName = CurrencyFieldType; //'Collect.vNext.FieldDefinitions.CurrencyFieldDefinition';
        this.testFormField12.displayName = 'Currency 2';
        this.testFormField12.required = true;
        this.testFormField12.maxValue = 50.00;
        this.testFormInstanceElement12.DecimalValue = 0.00;

        /*
        // Create a form group and a form array for some Reactive Form testing.
        this.testFormGroup = this.formBuilder.group({});
        this.testFormControl1 = new FormControl('');
        this.testFormControl2 = new FormControl('');
        this.testFormGroup.addControl('control 1', this.testFormControl1);
        this.testFormGroup.addControl('control 2', this.testFormControl2);
        this.testFormArray = this.formBuilder.array([]);
        this.testFormArray.push(this.testFormControl1);
        this.testFormArray.push(this.testFormControl2);
        */

        // Test drop-down list components/fields.
        this.testFormField13.fieldDefinitionClassName = DropDownFieldType; //'Collect.vNext.FieldDefinitions.DropDownFieldDefinition';
        this.testFormField13.displayName = 'Dropdown List 1';
        this.testFormField13.required = true;
        this.testFormField13.selectOptions = "Yes|No";
        this.testFormInstanceElement13.TextValue = 'No';

        this.testFormField14.fieldDefinitionClassName = DropDownFieldType; //'Collect.vNext.FieldDefinitions.DropDownFieldDefinition';
        this.testFormField14.displayName = 'Dropdown List 2';
        this.testFormField14.required = true;
        this.testFormField14.selectOptions = "Option 1|Option 2|Option 3|Option 4|Option 5";
        this.testFormInstanceElement14.TextValue = 'Option 1';

        // Test radio button fields.
        this.testFormField15.fieldDefinitionClassName = RadioButtonsFieldType; //'Collect.vNext.FieldDefinitions.RadioButtonFieldDefinition';
        this.testFormField15.displayName = 'Radio Buttons 1';
        this.testFormField15.required = true;
        this.testFormField15.selectOptions = "Yes|No";
        this.testFormInstanceElement15.TextValue = 'Yes';

        this.testFormField16.fieldDefinitionClassName = RadioButtonsFieldType; //'Collect.vNext.FieldDefinitions.RadioButtonFieldDefinition';
        this.testFormField16.displayName = 'Radio Buttons 2';
        this.testFormField16.required = true;
        this.testFormField16.selectOptions = "Option 1|Option 2|Option 3|Option 4|Option 5";
        this.testFormInstanceElement16.TextValue = 'Option 1';

        return;
        // END CODE TO REMOVE AFTER TESTING.
    }

    // Accessor methods.
    get SiteResponseManagementData(): SiteResponseManagementData {
        return (this.siteResponseManagementData);
    }

    get MetricFieldNames(): string[] {
        return (this.astrMetricFieldNames);
    }

    MetricName(strMetricFieldName: string): string {
        let strMetricName: string = 'Unknown Metric';

        switch (strMetricFieldName) {
            case 'distinctRecipientCount':
                strMetricName = 'Recipient Count';
                break;

            case 'distinctEditorsCount':
                strMetricName = 'Editor Count';
                break;

            case 'totalSubmissionsCount':
                strMetricName = 'Submission Count';
                break;

            case 'averageResponseTimeInHours':
                strMetricName = 'Average Response Time (hours)'
                break;

            default:
                break;
        }

        return (strMetricName);
    }

    MetricValue(strMetricFieldName: string): number {
        let metricValue: number = 0;

        if (this.siteResponseManagementData) {
            metricValue = this.siteResponseManagementData.siteManagementMetrics[strMetricFieldName];
        }

        return (metricValue);
    }

    get SubmissionsDataSource(): SubmissionsForReviewDataSource {
        return (this.submissionsDataSource); 
    }

    get SubmissionsTableColumnNames(): string[] {
        return (this.astrSubmissionsTableColNames);
    }

    // BEGIN CODE TO REMOVE AFTER TESTING.
    // Define getter/setter methods for FormInstanceElement,
    // required for using[(ngModel)] with the form instance element.
    get FormInstanceElement1(): FormInstanceElement {
        return (this.testFormInstanceElement1);
    }

    set FormInstanceElement1(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement2(): FormInstanceElement {
        return (this.testFormInstanceElement2);
    }

    set FormInstanceElement2(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement3(): FormInstanceElement {
        return (this.testFormInstanceElement3);
    }

    set FormInstanceElement3(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement4(): FormInstanceElement {
        return (this.testFormInstanceElement4);
    }

    set FormInstanceElement4(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement5(): FormInstanceElement {
        return (this.testFormInstanceElement5);
    }

    set FormInstanceElement5(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement6(): FormInstanceElement {
        return (this.testFormInstanceElement6);
    }

    set FormInstanceElement6(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement7(): FormInstanceElement {
        return (this.testFormInstanceElement7);
    }

    set FormInstanceElement7(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement8(): FormInstanceElement {
        return (this.testFormInstanceElement8);
    }

    set FormInstanceElement8(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement9(): FormInstanceElement {
        return (this.testFormInstanceElement9);
    }

    set FormInstanceElement9(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement10(): FormInstanceElement {
        return (this.testFormInstanceElement10);
    }

    set FormInstanceElement10(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement11(): FormInstanceElement {
        return (this.testFormInstanceElement11);
    }

    set FormInstanceElement11(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement12(): FormInstanceElement {
        return (this.testFormInstanceElement12);
    }

    set FormInstanceElement12(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement13(): FormInstanceElement {
        return (this.testFormInstanceElement13);
    }

    set FormInstanceElement13(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement14(): FormInstanceElement {
        return (this.testFormInstanceElement14);
    }

    set FormInstanceElement14(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement15(): FormInstanceElement {
        return (this.testFormInstanceElement15);
    }

    set FormInstanceElement15(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }

    get FormInstanceElement16(): FormInstanceElement {
        return (this.testFormInstanceElement16);
    }

    set FormInstanceElement16(formInstanceElementParam: FormInstanceElement) {
        // Note:  this method is a NOOP by design.

        return;
    }
    // END CODE TO REMOVE AFTER TESTING.

    // BEGIN CODE TO REMOVE AFTER TESTING.
    // Handle [(ngModel)] changes.
    handleNgOnChange(): void {
        return;
    }
    // END CODE TO REMOVE AFTER TESTING.
}
